import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import NoticeBar from '../ui/NoticeBar';
import { usePlan } from '../../contexts/PlanContext';
import {
  shouldShowRetentionNudge,
  isNudgeDismissed,
  markNudgeDismissed,
} from '../../utils/retentionNudge';
import {
  trackRetentionNudgeShown,
  trackRetentionNudgeDismissed,
  trackRetentionNudgeClicked,
} from '../../services/analyticsService';
import { TYPE } from '../../constants/typeScale';

interface Props {
  chatId: string;
  messageCount: number;
}

/**
 * Soft, dismissible upgrade nudge shown once a FREE user's chat gets long (a retention
 * signal). Not a gate — the user can dismiss and keep going. All show/dismiss logic lives
 * in utils/retentionNudge (unit-tested); this just renders + fires analytics.
 */
const RetentionNudge: React.FC<Props> = ({ chatId, messageCount }) => {
  const { planTier } = usePlan();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<boolean>(() => isNudgeDismissed(chatId));
  const shownRef = useRef<string>('');

  // This component is reused across chat switches (no remount key), so re-read the per-chat dismissed
  // flag whenever chatId changes — otherwise a nudge dismissed in chat A would stay suppressed in chat B
  // (and vice-versa), because the useState initializer only ran for the first chat.
  useEffect(() => { setDismissed(isNudgeDismissed(chatId)); }, [chatId]);

  const show = shouldShowRetentionNudge({ planTier, messageCount, dismissed });

  useEffect(() => {
    if (show && shownRef.current !== chatId) {
      shownRef.current = chatId;
      trackRetentionNudgeShown(chatId, messageCount);
    }
  }, [show, chatId, messageCount]);

  if (!show) return null;

  const dismiss = () => {
    markNudgeDismissed(chatId);
    setDismissed(true);
    trackRetentionNudgeDismissed(chatId);
  };

  const upgrade = () => {
    trackRetentionNudgeClicked(chatId);
    // Payments are live — go straight to the Wallet (subscribe + top-up), not the legacy
    // "opening soon" upgrade modal.
    navigate('/wallet');
  };

  return (
    <NoticeBar icon={<Sparkles className="w-4 h-4" />} className="mx-6 mb-2">
      <p className={`flex-1 ${TYPE.control} text-secondary-700 dark:text-secondary-300 leading-snug`}>
        Your team's getting a lot done here. Upgrade for more AI capacity, more storage, and smarter models.
      </p>
      <button
        onClick={upgrade}
        className={`shrink-0 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 ${TYPE.control} font-medium text-white dark:text-secondary-900 hover:opacity-90 transition-opacity`}
      >
        See plans
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss" title="Dismiss"
        className="shrink-0 rounded-md p-1 text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </NoticeBar>
  );
};

export default RetentionNudge;
